import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { Customer } from 'server/shared/models/customer';
import { SuperAdminSettingsInput } from 'server/shared/models/SuperAdminSettingsInput';
import { CustomerService } from '../../services/customer.service';
import { ToastService } from '../../services/toast/toast.service';
import { UserService } from '../../services/user.service';

interface BookingWebHookFormData {
  url: string;
  apiKey: string; // For new API key input (empty if unchanged)
  apiKeyPreview?: string; // For displaying existing key preview
}

@Component({
  selector: 'super-admin-settings',
  templateUrl: 'super-admin-settings.component.html',
  styleUrls: ['super-admin-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperAdminSettingsComponent implements OnInit {
  private destroyed$ = new Subject<void>();

  customer$ = new BehaviorSubject<Customer | undefined>(undefined);
  updatedFields$ = new BehaviorSubject<{
    bookingWebHook?: BookingWebHookFormData;
  }>({});
  changedFields = new Set<keyof SuperAdminSettingsInput>();

  constructor(
    private userService: UserService,
    private customerService: CustomerService,
    private toastService: ToastService,
    private router: Router,
  ) {
    this.userService.isAdmin$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isAdmin) => {
        if (!isAdmin) {
          this.router.navigate(['/dashboard']);
        }
      });
  }

  ngOnInit(): void {
    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.customer$.next(customer);
        this.setUpdatedFields();
      });
  }

  setUpdatedFields(): void {
    const customer = this.customer$.value;
    const updatedFields: { bookingWebHook?: BookingWebHookFormData } = {};

    updatedFields.bookingWebHook = customer?.bookingWebHook
      ? {
          url: customer.bookingWebHook.url || '',
          apiKey: '', // Empty for new input - user needs to enter new key to change it
          apiKeyPreview: (customer.bookingWebHook as any).apiKeyPreview || '',
        }
      : { url: '', apiKey: '' };

    this.updatedFields$.next(updatedFields);
    this.changedFields.clear();
  }

  onBookingWebHookChange(): void {
    this.changedFields.add('bookingWebHook');
  }

  saveSuperAdminCustomerSettings(): void {
    const customer = this.customer$.value;
    if (!customer) return;

    const updatedFields = this.updatedFields$.value;
    const fieldsToSave: SuperAdminSettingsInput = {};

    // Only include fields that have been changed
    if (this.changedFields.has('bookingWebHook')) {
      // Handle bookingWebHook: if both url and apiKey are empty, set to null to remove the field
      if (
        updatedFields.bookingWebHook &&
        updatedFields.bookingWebHook.url === '' &&
        !updatedFields.bookingWebHook.apiKey
      ) {
        fieldsToSave.bookingWebHook = null as any;
      } else if (updatedFields.bookingWebHook) {
        // Send the form data with apiKey (will be empty if user didn't change it)
        fieldsToSave.bookingWebHook = {
          url: updatedFields.bookingWebHook.url || '',
          apiKey: updatedFields.bookingWebHook.apiKey || '',
        } as any;
      }
    }

    // Add other settings here as they are added, checking changedFields.has('fieldName')

    Meteor.call(
      'saveSuperAdminCustomerSettings',
      {
        customerId: customer._id,
        updatedFields: fieldsToSave,
      },
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
          this.changedFields.clear();
          // Refresh the fields to show updated preview
          this.setUpdatedFields();
        }
      },
    );
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
